import React, { useEffect, useState } from "react";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { ActionButton, useToast } from "./ResultsPrimitives.jsx";
import ConfirmationModal from "./ConfirmationModal.jsx";

async function parseJsonSafely(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export default function RestartServiceButton({
  className = "",
  disabled = false,
  disabledTitle,
  size = "sm",
  label = "Railway",
  role,
  tabIndex,
}) {
  const { showToast, updateToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    async function loadAvailability() {
      try {
        const response = await fetchWithSupabaseAuth("/api/system/restart/availability", {
          method: "GET",
          cache: "no-store",
          headers: {
            Accept: "application/json",
          },
        });
        const payload = await parseJsonSafely(response);
        if (isCancelled) {
          return;
        }

        if (response.ok && payload && typeof payload === "object") {
          setAvailability(payload);
          return;
        }

        setAvailability({ available: false });
      } catch {
        if (!isCancelled) {
          setAvailability({ available: false });
        }
      }
    }

    void loadAvailability();

    return () => {
      isCancelled = true;
    };
  }, []);

  if (availability?.available !== true) {
    return null;
  }

  const closeConfirm = () => {
    if (!isLoading) {
      setIsConfirmOpen(false);
    }
  };

  const handleRestart = async () => {
    if (disabled || isLoading) {
      return;
    }

    setIsConfirmOpen(false);
    setIsLoading(true);

    const loadingToastId = showToast({
      type: "loading",
      title: "Redéploiement Railway en cours",
      message: "Le service va redémarrer. L'application sera indisponible pendant quelques instants.",
      duration: null,
    });

    try {
      const response = await fetchWithSupabaseAuth("/api/system/restart", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      const payload = await parseJsonSafely(response);

      if (!response.ok) {
        const detail = typeof payload?.detail === "string" && payload.detail.trim()
          ? payload.detail.trim()
          : "Erreur lors du redéploiement du service Railway.";
        throw new Error(detail);
      }

      const message = typeof payload?.message === "string" && payload.message.trim()
        ? payload.message.trim()
        : "Redéploiement Railway demandé.";

      updateToast(loadingToastId, {
        type: "success",
        title: "Redéploiement demandé",
        message: `${message} La page se rechargera automatiquement dans quelques secondes.`,
        duration: 5000,
      });
      window.setTimeout(() => {
        window.location.reload();
      }, 5000);
    } catch (error) {
      updateToast(loadingToastId, {
        type: "error",
        title: "Redéploiement impossible",
        message: error instanceof Error && error.message
          ? error.message
          : "Impossible de redéployer le service Railway.",
        duration: 8000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ActionButton
        role={role}
        tabIndex={tabIndex}
        size={size}
        tone="ghost"
        className={className}
        disabled={disabled || isLoading}
        title={disabledTitle || "Redéployer le service Railway (admin)"}
        onClick={() => {
          if (disabled || isLoading) {
            return;
          }
          setIsConfirmOpen(true);
        }}
      >
        <span>{isLoading ? "Redéploiement..." : label}</span>
      </ActionButton>

      <ConfirmationModal
        cancelLabel="Annuler"
        confirmLabel={isLoading ? "Redéploiement…" : "Reset Railway"}
        description="Aucune donnée métier n'est effacée : commandes, planning, journées dispatch et configuration restent en base PostgreSQL."
        eyebrow="Service Railway"
        impactItems={[
          "Seul le conteneur applicatif redémarre ; la base de données n'est pas touchée.",
          "L'application sera indisponible quelques instants (souvent moins d'une minute).",
          "Les caches mémoire du process sont perdus ; les données persistées sont rechargées au démarrage.",
        ]}
        isOpen={isConfirmOpen}
        onCancel={closeConfirm}
        onClose={closeConfirm}
        onConfirm={() => {
          void handleRestart();
        }}
        panelSize="compact"
        title="Confirmer le reset Railway ?"
        tone="danger"
        warningMessage="Les requêtes en cours pendant le redémarrage peuvent échouer ; il suffit de réessayer ensuite."
      />
    </>
  );
}
